import classnames from 'classnames';
import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { normalizeTestId } from '../../utils';
import './Tabs.css';

export interface Tab {
  /**
   * Unique identifier for the tab
   */
  id: string;
  /**
   * Text label displayed on the tab button
   */
  label: string;
  /**
   * Content to render in the tab panel when this tab is active
   */
  content: ReactNode;
  /**
   * Optional URL path for router-based tabs
   */
  path?: string;
}

export interface TabsProps {
  /**
   * Array of tab configurations
   */
  tabs: Tab[];
  /**
   * ID of the currently selected tab (for controlled usage)
   */
  selectedId?: string;
  /**
   * Callback when a tab is selected
   */
  onChange?: (selectedId: string) => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Base path for router-based navigation
   */
  basePath?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function Tabs({ tabs, selectedId, onChange, className, basePath, 'data-testid': dataTestId }: TabsProps) {
  const location = useLocation();
  const containerClasses = classnames('access-tabs', className);
  const baseTestId = dataTestId ?? 'tabs';

  const getActiveTabId = (): string => {
    if (basePath) {
      const currentPath = location.pathname;
      const matchedTab = tabs.find((tab) => tab.path && currentPath.includes(tab.path));

      return matchedTab?.id || tabs[0]?.id || '';
    }

    return selectedId || tabs[0]?.id || '';
  };

  const activeTabId = getActiveTabId();

  const handleTabClick = (tabId: string, path?: string) => {
    if (!basePath && onChange) {
      onChange(tabId);
    }
  };

  return (
    <div
      className={containerClasses}
      data-testid={baseTestId}>
      <div
        className='access-tabs__nav'
        data-testid={`${baseTestId}-nav`}
        role='tablist'>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const tabClasses = classnames('access-tabs__tab', {
            'access-tabs__tab--active': isActive,
          });

          const tabTestId = normalizeTestId(tab.id);

          if (basePath && tab.path) {
            return (
              <Link
                key={tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                aria-selected={isActive ? 'true' : 'false'}
                className={tabClasses}
                data-testid={`${baseTestId}-tab-${tabTestId}`}
                id={`tab-${tab.id}`}
                role='tab'
                to={`${basePath}${tab.path}`}
                onClick={() => handleTabClick(tab.id, tab.path)}>
                {tab.label}
              </Link>
            );
          }

          return (
            <button
              key={tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              aria-selected={isActive ? 'true' : 'false'}
              className={tabClasses}
              data-testid={`${baseTestId}-tab-${tabTestId}`}
              id={`tab-${tab.id}`}
              role='tab'
              type='button'
              onClick={() => handleTabClick(tab.id)}>
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        className='access-tabs__content'
        data-testid={`${baseTestId}-content`}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const tabTestId = normalizeTestId(tab.id);

          return (
            <div
              key={tab.id}
              aria-labelledby={`tab-${tab.id}`}
              className={classnames('access-tabs__panel', {
                'access-tabs__panel--active': isActive,
              })}
              data-testid={`${baseTestId}-panel-${tabTestId}`}
              hidden={!isActive}
              id={`tabpanel-${tab.id}`}
              role='tabpanel'>
              {tab.content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Tabs;
